import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { TrendingUp, Calendar, Award, Flame } from 'lucide-react';
import PRTracking from './PRTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AthleteDashboard({ user }) {
  const [ratings, setRatings] = useState([]);
  const [attendance, setAttendance] = useState({ classes: [], total_bookings: 0, total_attended: 0 });
  const [streak, setStreak] = useState({ current_streak: 0, total_classes: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ratingsRes, attendanceRes, streakRes] = await Promise.all([
        axios.get(`${API}/ratings/my-ratings`),
        axios.get(`${API}/users/${user.id}/attendance`),
        axios.get(`${API}/athletes/streak`)
      ]);
      setRatings(ratingsRes.data);
      setAttendance(attendanceRes.data);
      setStreak(streakRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  const averageRating = ratings.length > 0
    ? (ratings.reduce((sum, r) => sum + r.rating.rating, 0) / ratings.length).toFixed(1)
    : 0;

  const attendanceClasses = attendance.classes || [];

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="athlete-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2">
            My Stats
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">Track your progress and performance</p>
        </div>

        {/* Streak Banner */}
        {streak.current_streak > 0 && (
          <Card className="bg-gradient-to-r from-[#CCFF00]/10 to-[#F97316]/10 border-[#CCFF00]/30 p-6 mb-8" data-testid="streak-banner">
            <div className="flex items-center gap-4">
              <div className="bg-[#CCFF00]/20 p-4 rounded-full">
                <Flame className="w-10 h-10 text-[#CCFF00]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA] uppercase tracking-wide mb-1">Weekly Attendance Streak</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-heading font-black text-[#CCFF00]">{streak.current_streak}</span>
                  <span className="text-xl text-[#A1A1AA] font-heading uppercase">
                    {streak.current_streak === 1 ? 'week' : 'weeks'}
                  </span>
                </div>
                <p className="text-sm text-[#A1A1AA] mt-1">Keep it up! Consistency is key.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <Card className="bg-[#18181B] border-[#27272A] p-6" data-testid="streak-card">
            <div className="flex items-center gap-4">
              <div className="bg-[#F97316]/10 p-3 rounded-sm">
                <Flame className="w-8 h-8 text-[#F97316]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA] uppercase tracking-wide mb-1">Week Streak</p>
                <p className="text-4xl font-heading font-black text-[#FAFAFA]">{streak.current_streak}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-[#18181B] border-[#27272A] p-6" data-testid="total-ratings-card">
            <div className="flex items-center gap-4">
              <div className="bg-[#CCFF00]/10 p-3 rounded-sm">
                <TrendingUp className="w-8 h-8 text-[#CCFF00]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA] uppercase tracking-wide mb-1">WODs Rated</p>
                <p className="text-4xl font-heading font-black text-[#FAFAFA]">{ratings.length}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-[#18181B] border-[#27272A] p-6" data-testid="avg-difficulty-card">
            <div className="flex items-center gap-4">
              <div className="bg-[#2563EB]/10 p-3 rounded-sm">
                <Award className="w-8 h-8 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA] uppercase tracking-wide mb-1">Avg Difficulty</p>
                <p className="text-4xl font-heading font-black text-[#FAFAFA]">{averageRating}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-[#18181B] border-[#27272A] p-6" data-testid="classes-attended-card">
            <div className="flex items-center gap-4">
              <div className="bg-[#22C55E]/10 p-3 rounded-sm">
                <Calendar className="w-8 h-8 text-[#22C55E]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA] uppercase tracking-wide mb-1">Attended</p>
                <p className="text-4xl font-heading font-black text-[#FAFAFA]">{attendance.total_attended || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Personal Records */}
        <div className="mb-12">
          <PRTracking user={user} />
        </div>

        {/* WOD Rating History */}
        <div className="mb-12">
          <h2 className="text-3xl font-heading font-bold uppercase tracking-tight text-[#FAFAFA] mb-6 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-[#CCFF00]" />
            WOD Rating History
          </h2>
          {ratings.length === 0 ? (
            <Card className="bg-[#18181B] border-[#27272A] p-8" data-testid="no-ratings-message">
              <p className="text-[#A1A1AA] text-center text-lg">You haven't rated any WODs yet.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ratings.map((item) => (
                <Card key={item.rating.id} className="bg-[#18181B] border-[#27272A] p-5" data-testid={`rating-card-${item.rating.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA] mb-1">
                        {item.wod.title}
                      </h3>
                      <p className="text-sm text-[#A1A1AA]">
                        {new Date(item.wod.date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-center bg-[#CCFF00]/10 px-3 py-2 rounded-sm">
                      <span className="text-2xl font-heading font-black text-[#CCFF00]">
                        {item.rating.rating}
                      </span>
                      <span className="text-xs text-[#A1A1AA] uppercase">/ 10</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#27272A]">
                    <p className="text-xs text-[#A1A1AA]">
                      Rated {new Date(item.rating.rated_at).toLocaleDateString()}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Attendance History */}
        <div>
          <h2 className="text-3xl font-heading font-bold uppercase tracking-tight text-[#FAFAFA] mb-6 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-[#CCFF00]" />
            Attendance History
          </h2>
          {attendanceClasses.length === 0 ? (
            <Card className="bg-[#18181B] border-[#27272A] p-8" data-testid="no-attendance-message">
              <p className="text-[#A1A1AA] text-center text-lg">You haven't attended any classes yet.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {attendanceClasses.map((cls, index) => (
                <Card key={cls.booking_id || index} className="bg-[#18181B] border-[#27272A] p-5" data-testid={`attendance-card-${index}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA] mb-1">
                        {cls.title}
                      </h3>
                      <p className="text-sm text-[#A1A1AA]">
                        {new Date(cls.date).toLocaleDateString()} {cls.start_time && `\u2022 ${cls.start_time}`}
                        {cls.end_time && ` - ${cls.end_time}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {cls.checked_in ? (
                        <>
                          <span className="w-2 h-2 bg-[#22C55E] rounded-full"></span>
                          <span className="text-sm text-[#22C55E] font-semibold uppercase">Attended</span>
                        </>
                      ) : (
                        <>
                          <span className="w-2 h-2 bg-[#F97316] rounded-full"></span>
                          <span className="text-sm text-[#F97316] font-semibold uppercase">Booked</span>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
